/**
 * Scheduled Runs Tab Component
 * 
 * Displays and manages scheduled sync tasks with timezone support.
 * Server stores times in UTC, UI displays in user's local timezone.
 * 
 * Features:
 * - View and manage scheduled sync tasks
 * - Edit schedule times with local-to-UTC conversion
 * - Trigger manual syncs with real-time progress tracking
 * - Monitor Celery Beat scheduler status
 * - View sync history from database with live progress overlay
 * - Country-based filtering
 * 
 * @module InstrumentMaster/tabs/ScheduledRunsTab
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import WebSocketManager from '../../../../websocket/WebSocketManager';
import { 
  FiRefreshCw, 
  FiClock,
  FiActivity,
  FiEdit2,
  FiSave,
  FiX,
  FiPower,
  FiAlertTriangle,
  FiDatabase,
  FiCheck,
  FiXCircle,
  FiLoader
} from 'react-icons/fi';
import { 
  formatScheduleTime, 
  formatDayOfWeek, 
  getRelativeTime, 
  getUserTimezone,
  utcToLocal,
  localTimeToUtc 
} from '../../../../utils/timezone';

const ScheduledRunsTab = () => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [serverTime, setServerTime] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({ hour: 0, minute: 0, dayOfWeek: '*', isEnabled: true });
  const [countryFilter, setCountryFilter] = useState('all');
  const [supportsRestart, setSupportsRestart] = useState(false);
  const [beatStatus, setBeatStatus] = useState(null);
  const [beatPid, setBeatPid] = useState(null);
  const [brokerMetadata, setBrokerMetadata] = useState([]);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastRestartUtc, setLastRestartUtc] = useState(null);
  // [UPDATED 2025-11-30]: Wildcard WebSocket subscription for all sync progress updates
  // runningSyncs uses sync_id as key for accurate tracking across all sources
  const [runningSyncs, setRunningSyncs] = useState({}); // Map of sync_id -> { brokerSlug, stage, percentage, message, processed, total, isActive, source }
  // Database-backed sync history from instrument_sync_log table
  const [syncHistory, setSyncHistory] = useState([]); // Array of sync log records from database
  const [syncHistoryLoading, setSyncHistoryLoading] = useState(false);
  const [syncHistoryFilter, setSyncHistoryFilter] = useState({ source: '', status: '' });
  const userTimezone = getUserTimezone();

  /**
   * Wildcard WebSocket subscription for ALL sync progress updates
   * Receives updates from any sync regardless of who triggered it (user or scheduled)
   */
  useEffect(() => {
    const handleSyncUpdate = (data) => {
      console.log('📡 [ScheduledRuns] Wildcard sync update:', data);
      
      if (!data || !data.sync_id) {
        console.warn('⚠️ Received sync update without sync_id:', data);
        return;
      }

      const syncId = data.sync_id;
      const source = data.source || data.broker_slug || 'unknown';
      const brokerSlug = source.toUpperCase();
      const stage = data.stage || data.status || 'syncing';
      const percentage = typeof data.progress_percentage === 'number' 
        ? data.progress_percentage 
        : (typeof data.percentage === 'number' ? data.percentage : 0);

      // Extract master + source counters for live UI stats
      const sourceStatsPayload = data.source_stats || data.sourceStats || null;
      const derivedSourceProcessed = sourceStatsPayload?.processed ?? (stage === 'updating_source' ? (data.processed_records ?? data.current_count ?? 0) : undefined);
      const derivedSourceTotal = sourceStatsPayload?.total ?? (stage === 'updating_source' ? (data.total_records ?? data.total_count ?? 0) : undefined);
      const mergedSourceStats = sourceStatsPayload
        ? { ...sourceStatsPayload, processed: derivedSourceProcessed ?? sourceStatsPayload.processed, total: derivedSourceTotal ?? sourceStatsPayload.total }
        : undefined;

      const masterTotal = stage === 'updating_source'
        ? undefined
        : (data.total_instruments ?? data.total_count ?? data.total_records);
      const masterProcessed = stage === 'updating_source'
        ? undefined
        : (data.processed_records ?? data.current_count);
      const masterAdded = data.added_count ?? data.master_added;
      const masterUpdated = data.updated_count ?? data.master_updated;
      const masterSkipped = data.skipped_count ?? data.master_skipped;
      const failedCount = data.failed_count;

      // Keep sync history in sync with live updates: insert or update a history record
      try {
        setSyncHistory(prev => {
          if (!syncId) return prev;
          const idx = prev.findIndex(l => l.sync_id === syncId);
          const nowIso = new Date().toISOString();
          const mappedStatus = (stage === 'completed') ? 'COMPLETED' : (stage === 'failed' || stage === 'error') ? 'FAILED' : (stage === 'initializing' || stage === 'pending') ? 'PENDING' : 'IN_PROGRESS';

          // Build a minimal history record from available data
          const newRecord = {
            sync_id: syncId,
            started_at: data.started_at || (idx === -1 ? nowIso : (prev[idx] && prev[idx].started_at) || nowIso),
            broker_id: data.broker_id ?? (data.broker_id === 0 ? 0 : undefined),
            app_name: data.broker_display_name || data.broker_slug || data.app_name || brokerSlug,
            sync_type: data.sync_type || (data.triggered_by ? 'manual' : 'manual'),
            status: mappedStatus,
            total_instruments: data.total_records ?? data.total_count ?? (prev[idx] && prev[idx].total_instruments) ?? 0,
            added_count: data.added_count ?? (prev[idx] && prev[idx].added_count) ?? 0,
            updated_count: data.updated_count ?? (prev[idx] && prev[idx].updated_count) ?? 0,
            failed_count: data.failed_count ?? (prev[idx] && prev[idx].failed_count) ?? 0,
            duration_seconds: (stage === 'completed' && prev[idx] && prev[idx].started_at)
              ? Math.max(0, Math.round((new Date().getTime() - new Date(prev[idx].started_at).getTime()) / 1000))
              : (prev[idx] && prev[idx].duration_seconds) || null
          };

          // If found, update in place (preserve order), otherwise prepend
          if (idx !== -1) {
            const copy = [...prev];
            copy[idx] = { ...copy[idx], ...newRecord };
            return copy;
          }
          return [newRecord, ...prev];
        });
      } catch (e) {
        console.warn('⚠️ [ScheduledRuns] Failed to sync history with live update', e);
      }

      // Update running syncs state
      if (stage === 'completed') {
        setRunningSyncs(prev => ({
          ...prev,
          [syncId]: {
            syncId,
            brokerSlug,
            stage: 'completed',
            percentage: 100,
            message: `Completed! ${data.total_instruments || data.master_instruments_saved || 'All'} instruments synced`,
            processed: data.total_instruments || data.master_instruments_saved || 0,
            total: data.total_instruments || data.master_instruments_saved || 0,
            master_processed: data.total_instruments || data.master_instruments_saved || 0,
            master_total: data.total_instruments || data.master_instruments_saved || 0,
            master_added: masterAdded ?? data.added_count ?? 0,
            master_updated: masterUpdated ?? data.updated_count ?? 0,
            master_skipped: masterSkipped ?? data.skipped_count ?? 0,
            failed_count: failedCount ?? data.failed_count ?? 0,
            source_stats: mergedSourceStats,
            source_processed: derivedSourceProcessed,
            source_total: derivedSourceTotal,
            isActive: false,
            source: brokerSlug
          }
        }));
        
        // Clear completed sync after 10 seconds and refresh history
        setTimeout(() => {
          setRunningSyncs(prev => {
            const updated = { ...prev };
            delete updated[syncId];
            return updated;
          });
          // Refresh sync history to show latest completed sync
          fetchSyncHistory();
        }, 10000);
        return;
      }

      if (stage === 'failed' || stage === 'error') {
        setRunningSyncs(prev => ({
          ...prev,
          [syncId]: {
            syncId,
            brokerSlug,
            stage: 'failed',
            percentage,
            message: data.error || data.message || 'Sync failed',
            processed: (masterProcessed ?? data.processed_records ?? 0),
            total: (masterTotal ?? data.total_records ?? 0),
            master_processed: (masterProcessed ?? data.processed_records ?? 0),
            master_total: (masterTotal ?? data.total_records ?? 0),
            master_added: masterAdded ?? data.added_count ?? 0,
            master_updated: masterUpdated ?? data.updated_count ?? 0,
            master_skipped: masterSkipped ?? data.skipped_count ?? 0,
            failed_count: failedCount ?? data.failed_count ?? 0,
            source_stats: mergedSourceStats,
            source_processed: derivedSourceProcessed,
            source_total: derivedSourceTotal,
            isActive: false,
            source: brokerSlug
          }
        }));
        
        // Clear failed sync after 15 seconds and refresh history
        setTimeout(() => {
          setRunningSyncs(prev => {
            const updated = { ...prev };
            delete updated[syncId];
            return updated;
          });
          fetchSyncHistory();
        }, 15000);
        return;
      }

      // Update progress (track master + source separately so UI places stats correctly)
      setRunningSyncs(prev => {
        const prevEntry = prev[syncId] || {};

        const nextMasterProcessed = (stage === 'updating_source')
          ? (prevEntry.master_processed ?? prevEntry.processed ?? 0)
          : (masterProcessed ?? prevEntry.master_processed ?? prevEntry.processed ?? 0);
        const nextMasterTotal = (stage === 'updating_source')
          ? (prevEntry.master_total ?? prevEntry.total ?? masterTotal ?? data.total_count ?? data.total_records ?? 0)
          : (masterTotal ?? prevEntry.master_total ?? prevEntry.total ?? data.total_count ?? data.total_records ?? 0);

        const nextSourceProcessed = derivedSourceProcessed ?? prevEntry.source_processed;
        const nextSourceTotal = derivedSourceTotal ?? prevEntry.source_total;

        const nextEntry = {
          ...prevEntry,
          syncId,
          brokerSlug,
          stage,
          percentage,
          message: data.message || `${stage}...`,
          processed: nextMasterProcessed,
          total: nextMasterTotal,
          master_processed: nextMasterProcessed,
          master_total: nextMasterTotal,
          master_added: masterAdded ?? prevEntry.master_added ?? prevEntry.added_count ?? 0,
          master_updated: masterUpdated ?? prevEntry.master_updated ?? prevEntry.updated_count ?? 0,
          master_skipped: masterSkipped ?? prevEntry.master_skipped ?? prevEntry.skipped_count ?? 0,
          failed_count: failedCount ?? prevEntry.failed_count ?? 0,
          source_stats: mergedSourceStats ?? prevEntry.source_stats,
          source_processed: nextSourceProcessed,
          source_total: nextSourceTotal,
          isActive: true,
          source: brokerSlug
        };

        // When stage is updating_source, keep master counters intact but still expose source stats for Source column
        if (stage === 'updating_source') {
          nextEntry.processed = nextMasterProcessed;
          nextEntry.total = nextMasterTotal;
        }

        return {
          ...prev,
          [syncId]: nextEntry
        };
      });
    };

    // Subscribe to wildcard topic for all sync updates
    console.log('📡 [ScheduledRuns] Subscribing to instrument-sync.* wildcard');
    const unsubscribe = WebSocketManager.subscribeTopic('instrument-sync.*', {
      onData: handleSyncUpdate,
      onAck: (ack) => console.log('✅ [ScheduledRuns] Wildcard subscription ack:', ack),
      onError: (error) => console.error('❌ [ScheduledRuns] Wildcard subscription error:', error),
    });

    return () => {
      console.log('📡 [ScheduledRuns] Unsubscribing from wildcard');
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to find running sync by broker slug
  const getRunningSync = useCallback((brokerSlug) => {
    if (!brokerSlug) return null;
    const key = brokerSlug.toUpperCase();
    // Find any running sync for this broker
    return Object.values(runningSyncs).find(sync => sync.brokerSlug === key && sync.isActive);
  }, [runningSyncs]);

  // Get country flag URL for display (similar to ViewTradeAccounts)
  const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode === 'ALL' || countryCode === 'UNKNOWN') return null;
    const code = countryCode.toLowerCase();
    return `https://flagcdn.com/24x18/${code}.png`;
  };

  const resetEditForm = () => {
    setEditForm({ hour: 0, minute: 0, dayOfWeek: '*', isEnabled: true });
  };

  const dayOfWeekOptions = useMemo(() => (
    [
      { value: '*', label: 'Every day' },
      { value: '0', label: 'Sunday' },
      { value: '1', label: 'Monday' },
      { value: '2', label: 'Tuesday' },
      { value: '3', label: 'Wednesday' },
      { value: '4', label: 'Thursday' },
      { value: '5', label: 'Friday' },
      { value: '6', label: 'Saturday' }
    ]
  ), []);

  /**
   * Trigger manual sync for a specific schedule row
   * Progress updates are received via the wildcard subscription automatically
   * @param {object} schedule - The schedule object containing broker_slug
   */
  const triggerRowSync = async (schedule) => {
    if (!schedule.broker_slug) {
      showSnackbar('Cannot trigger sync: No broker associated with this schedule', 'error');
      return;
    }

    const brokerSlug = schedule.broker_slug;
    const key = brokerSlug.toUpperCase();

    // Check if sync is already running for this broker
    const existingSync = getRunningSync(brokerSlug);
    if (existingSync) {
      showSnackbar(`Sync already running for ${key}`, 'warning');
      return;
    }

    try {
      // Trigger the sync via API
      const response = await services.instruments.admin.triggerSync(brokerSlug);
      const syncId = response.data.sync_id;

      showSnackbar(`Sync initiated for ${key}. Sync ID: ${syncId.substring(0, 8)}...`, 'success');

      // Initialize sync state - progress updates will come via wildcard subscription
      setRunningSyncs(prev => ({
        ...prev,
        [syncId]: {
          syncId,
          brokerSlug: key,
          stage: 'initializing',
          percentage: 0,
          message: 'Sync initiated, waiting for progress...',
          processed: 0,
          total: 0,
          isActive: true,
          source: key
        }
      }));

      // Prepend a minimal history record so the History table shows immediately
      try {
        const nowIso = new Date().toISOString();
        const initialLog = {
          sync_id: syncId,
          started_at: nowIso,
          broker_id: schedule?.broker_id ?? 0,
          app_name: schedule?.broker_display_name || schedule?.broker_slug || schedule?.broker_slug?.toUpperCase() || 'Exchange',
          sync_type: 'manual',
          status: 'PENDING',
          total_instruments: 0,
          added_count: 0,
          updated_count: 0,
          failed_count: 0,
          duration_seconds: null
        };

        setSyncHistory(prev => {
          if (!syncId) return prev;
          const exists = prev.some(l => l.sync_id === syncId);
          if (exists) return prev;
          return [initialLog, ...prev];
        });
      } catch (e) {
        console.warn('⚠️ [ScheduledRuns] Failed to add initial sync history record', e);
      }

    } catch (error) {
      showSnackbar(error.message || `Failed to trigger sync for ${key}`, 'error');
    }
  };

  const countryNameLookup = useMemo(() => {
    const map = new Map();
    brokerMetadata.forEach((broker) => {
      if (broker.country_code) {
        map.set(
          broker.country_code.toUpperCase(),
          broker.country_name || broker.country_code.toUpperCase()
        );
      }
    });
    schedules.forEach((schedule) => {
      if (schedule.country_code && schedule.country_name) {
        map.set(schedule.country_code.toUpperCase(), schedule.country_name);
      }
    });
    map.set('ALL', 'All Regions');
    map.set('UNKNOWN', 'Unknown');
    return map;
  }, [brokerMetadata, schedules]);

  const getCountryName = (countryCode, fallbackName) => {
    if (!countryCode) {
      return fallbackName || 'Unknown';
    }
    const normalized = countryCode.toUpperCase();
    if (countryNameLookup.has(normalized)) {
      return countryNameLookup.get(normalized);
    }
    const builtinNames = {
      IN: 'India',
      US: 'USA',
      UK: 'United Kingdom',
      ALL: 'All Regions',
      UNKNOWN: 'Unknown',
    };
    return fallbackName || builtinNames[normalized] || normalized;
  };

  const countryOptions = useMemo(() => {
    const codes = new Set();
    schedules.forEach((schedule) => {
      const code = schedule.country_code
        ? schedule.country_code.toUpperCase()
        : 'UNKNOWN';
      codes.add(code);
    });
    if (!codes.has('ALL')) {
      codes.add('ALL');
    }
    return Array.from(codes).sort();
  }, [schedules]);

  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => {
      if (countryFilter === 'all') return true;
      const scheduleCountry = schedule.country_code
        ? schedule.country_code.toUpperCase()
        : 'UNKNOWN';
      if (scheduleCountry === 'ALL') {
        return true;
      }
      return scheduleCountry === countryFilter;
    });
  }, [schedules, countryFilter]);

  const formatLastSyncStatus = (status) => {
    if (!status) {
      return null;
    }
    const normalized = status.toString().replace(/_/g, ' ').toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  // Fetch scheduled tasks
  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const response = await services.instruments.admin.getScheduledTasks();
      const data = response.data || {};
      setSchedules(data.schedules || []);
      setServerTime(data.server_time_utc || null);
      setSupportsRestart(Boolean(data.supports_restart));
      setBeatStatus(data.beat_status || null);
      setBeatPid(data.beat_pid ?? null);
      setLastRestartUtc(data.last_restart_utc || null);
      setBrokerMetadata(data.brokers || []);
    } catch (error) {
      const errorMessage = error.message || error.detail || 'Failed to fetch scheduled tasks';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Cleanup stale syncs that may have been interrupted (called on initial load)
  const cleanupStaleSyncs = async () => {
    try {
      const response = await services.instruments.admin.markStaleSyncs(15); // 15 minutes threshold
      const data = response.data || {};
      if (data.marked_count > 0) {
        console.log(`✅ [ScheduledRuns] Cleaned up ${data.marked_count} stale syncs`);
        // Refresh history after cleanup
        await fetchSyncHistory();
      }
    } catch (error) {
      // Silently fail - this is a background cleanup
      console.warn('⚠️ [ScheduledRuns] Failed to cleanup stale syncs:', error.message);
    }
  };

  // Fetch sync history from database
  const fetchSyncHistory = async () => {
    setSyncHistoryLoading(true);
    try {
      const params = {
        limit: 25,
        skip: 0,
        ...(syncHistoryFilter.source && { source: syncHistoryFilter.source }),
        ...(syncHistoryFilter.status && { status: syncHistoryFilter.status })
      };
      const response = await services.instruments.admin.getSyncHistory(params);
      setSyncHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sync history:', error);
      // Don't show snackbar for history errors to avoid noise
    } finally {
      setSyncHistoryLoading(false);
    }
  };

  const restartCeleryBeat = async () => {
    if (!supportsRestart || isRestarting) {
      return;
    }

    setIsRestarting(true);
    try {
      const response = await services.instruments.admin.restartSchedules();
      const data = response.data || {};
      showSnackbar(data.message || 'Celery Beat restarted successfully.', 'success');

      if (typeof data.success === 'boolean') {
        setBeatStatus(data.success ? 'running' : 'stopped');
      }
      if (Object.prototype.hasOwnProperty.call(data, 'started_pid') || Object.prototype.hasOwnProperty.call(data, 'stopped_pid')) {
        setBeatPid(data.started_pid ?? data.stopped_pid ?? null);
      }
      if (data.restarted_at_utc) {
        setLastRestartUtc(data.restarted_at_utc);
      }
    } catch (error) {
      const errorMessage = error.message || error.detail || 'Failed to restart Celery Beat';
      showSnackbar(errorMessage, 'error');
    } finally {
      try {
        await fetchSchedules();
      } catch (refreshError) {
        // fetchSchedules already reports errors via snackbar
      }
      setIsRestarting(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
    fetchSyncHistory();
    // Cleanup any stale syncs on initial load
    cleanupStaleSyncs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start editing a schedule
  const startEditing = (schedule) => {
    const config = schedule.schedule_config || {};
    const utcHour = config.hour !== '*' ? parseInt(config.hour, 10) : 0;
    const utcMinute = config.minute !== '*' ? parseInt(config.minute, 10) : 0;
    const dayOfWeek = (config.day_of_week ?? '*').toString();
    const initialEnabled = schedule.schedule_type === 'manual'
      ? true
      : (typeof schedule.is_enabled === 'boolean' ? schedule.is_enabled : true);

    const now = new Date();
    const utcDate = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      utcHour,
      utcMinute,
      0
    ));
    
    setEditForm({
      hour: utcDate.getHours(),
      minute: utcDate.getMinutes(),
      dayOfWeek,
      isEnabled: initialEnabled
    });
    setEditingTask(schedule);
  };

  // Save schedule changes
  const saveSchedule = async (schedule) => {
    if (!schedule) {
      return;
    }

    try {
      setIsSaving(true);
      const { utcHour, utcMinute } = localTimeToUtc(editForm.hour, editForm.minute);
      const day_of_week = editForm.dayOfWeek || '*';

      if (schedule.schedule_type === 'manual') {
        if (!schedule.broker_slug) {
          showSnackbar('Cannot schedule this task: broker information missing.', 'error');
          return;
        }

        await services.instruments.admin.createDynamicSchedule({
          broker_slug: schedule.broker_slug,
          hour: utcHour,
          minute: utcMinute,
          day_of_week,
        });

        const brokerLabel = schedule.broker_display_name || schedule.broker_slug?.toUpperCase() || 'broker';
        showSnackbar(`${brokerLabel} schedule created. Restart Celery Beat to activate the new timing.`, 'success');
      } else {
        await services.instruments.admin.updateScheduledTask(schedule.task_id, {
          hour: utcHour,
          minute: utcMinute,
          day_of_week,
          is_enabled: editForm.isEnabled,
        });

        showSnackbar('Schedule updated successfully. Changes will take effect automatically within 30 seconds.', 'success');
      }

      setEditingTask(null);
      resetEditForm();
      fetchSchedules();
    } catch (error) {
      showSnackbar(error.message || 'Failed to save schedule', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingTask(null);
    resetEditForm();
  };

  // eslint-disable-next-line no-unused-vars -- Future feature: toggle schedule enabled/disabled
  const handleToggleEnabled = async (schedule) => {
    if (!schedule || schedule.schedule_type === 'manual') {
      return;
    }

    const config = schedule.schedule_config || {};
    const dayOfWeek = (config.day_of_week ?? '*').toString();
    const nextState = !schedule.is_enabled;

    try {
      await services.instruments.admin.updateScheduledTask(schedule.task_id, {
        is_enabled: nextState,
        day_of_week: dayOfWeek,
      });

      const actionLabel = nextState ? 'enabled' : 'disabled';
      showSnackbar(`Schedule ${actionLabel} for ${schedule.task_id}. Restart Celery Beat to apply changes.`, 'success');
      fetchSchedules();
    } catch (error) {
      showSnackbar(error.message || 'Failed to update schedule state', 'error');
    }
  };

  // eslint-disable-next-line no-unused-vars -- Future feature: delete schedule
  const handleDeleteSchedule = async (schedule) => {
    if (!schedule) {
      return;
    }

    try {
      await services.instruments.admin.deleteScheduledTask(schedule.task_id);
      showSnackbar(`Schedule '${schedule.task_id}' removed. Restart Celery Beat to finalize.`, 'success');
      fetchSchedules();
    } catch (error) {
      showSnackbar(error.message || 'Failed to delete schedule', 'error');
    }
  };

  return (
    <div className="space-y-4">
      {/* Compact Header Bar with Scheduler Status & Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white dark:bg-bg-1 rounded-xl border border-[var(--divider)] shadow-sm">
        {/* Scheduler Status */}
        <div className="flex items-center gap-4">
          {supportsRestart && (
            <div className="flex items-center gap-3">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                  beatStatus === 'running'
                    ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                <FiActivity className="text-lg" />
              </div>
              <div className="text-sm">
                <span className="font-medium text-fg-2">Scheduler: </span>
                <span className={`font-semibold ${
                  beatStatus === 'running'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {(beatStatus || 'unknown').toUpperCase()}
                </span>
                {beatPid && <span className="text-fg-3 ml-1 text-xs">PID {beatPid}</span>}
                {lastRestartUtc && (
                  <span className="text-fg-3 ml-2 text-xs" title={`Last restart: ${lastRestartUtc}`}>
                    • Restarted {getRelativeTime(lastRestartUtc)}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={restartCeleryBeat}
                disabled={isRestarting}
                title="Force restart scheduler (changes are applied automatically within 30 seconds)"
                className="btn btn-secondary btn-sm"
              >
                <FiPower className={isRestarting ? 'animate-spin text-sm' : 'text-sm'} />
                {isRestarting ? 'Restarting...' : 'Force Restart'}
              </button>
            </div>
          )}
          
          {/* Timezone & Server Time */}
          <div className="flex items-center gap-2 text-xs text-fg-2 border-l border-[var(--divider)] pl-4">
            <FiClock className="text-blue-500" />
            <span>Timezone: <strong className="text-fg-2">{userTimezone}</strong></span>
            {serverTime && (
              <span className="hidden sm:inline">• Server: {new Date(serverTime).toUTCString().slice(0, -4)}</span>
            )}
          </div>
        </div>

        {/* Task Type Legend (Compact) */}
        <div className="flex items-center gap-3 text-xs">
          <span className="ds-badge neutral xs">
            ⚙️ Hardcoded
          </span>
          <span className="ds-badge info xs">
            🔍 Dynamic
          </span>
        </div>
      </div>

      {/* Filters & Statistics Row */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          {/* Country Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-fg-2">Country:</label>
            <select
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="ds-input"
            >
              <option value="all">All Countries</option>
              {countryOptions.map((code) => (
                <option key={code} value={code}>
                  {getCountryName(code)} ({code})
                </option>
              ))}
            </select>
          </div>
          
          {/* Task Statistics */}
          <div className="flex items-center gap-3 text-xs text-fg-2">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-400"></span>
              Hardcoded: <strong className="text-fg-2">{filteredSchedules.filter(s => s.task_source === 'hardcoded').length}</strong>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              Dynamic: <strong className="text-fg-2">{filteredSchedules.filter(s => s.task_source === 'dynamic').length}</strong>
            </span>
            <span>
              Total: <strong className="text-fg-2">{filteredSchedules.length}</strong>
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={fetchSchedules}
          disabled={loading}
          className="btn btn-ghost btn-sm flex items-center gap-1.5"
        >
          <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Scheduled Tasks Table */}
      <div className="bg-white dark:bg-bg-1 rounded-xl border border-[var(--divider)] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-bg-3">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                Task
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                Broker
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                Country
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                Schedule (Local)
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                Next Run
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                Last Run
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-bg-1 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-fg-2">
                  <div className="flex justify-center items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    Loading scheduled tasks...
                  </div>
                </td>
              </tr>
            ) : filteredSchedules.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-fg-2">
                  {countryFilter === 'all' 
                    ? 'No scheduled tasks found.' 
                    : `No scheduled tasks found for ${getCountryName(countryFilter)}.`}
                </td>
              </tr>
            ) : (
              filteredSchedules.map((schedule) => {
                const config = schedule.schedule_config || {};
                const timeInfo = formatScheduleTime(config.hour, config.minute);
                const dayInfo = formatDayOfWeek(config.day_of_week);
                const isEditing = editingTask?.task_id === schedule.task_id;
                const isManual = schedule.schedule_type === 'manual';
                const scheduleCountry = schedule.country_code
                  ? schedule.country_code.toUpperCase()
                  : 'UNKNOWN';
                const countryName = getCountryName(scheduleCountry, schedule.country_name);
                // Use getRunningSync to find any active sync for this broker
                const syncProgress = getRunningSync(schedule.broker_slug);
                const flagUrl = getCountryFlag(scheduleCountry);
                const lastSyncStatusLabel = formatLastSyncStatus(schedule.last_sync_status);
                const brokerSlugLabel = schedule.broker_slug ? schedule.broker_slug.toUpperCase() : null;
                const brokerUnavailable = schedule.broker_is_available === false;

                const rowClassName = `hover:bg-bg-3/50 ${brokerUnavailable ? 'bg-red-50/70 dark:bg-red-900/20' : ''}`;

                return (
                  <tr key={schedule.task_id} className={rowClassName}>
                    {/* Task Name & Badges */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-fg-1">{schedule.task_id}</span>
                        {schedule.task_source === 'dynamic' ? (
                          <span className="ds-badge info xs">
                            🔍
                          </span>
                        ) : (
                          <span className="ds-badge neutral xs">
                            ⚙️
                          </span>
                        )}
                        {brokerUnavailable && (
                          <FiAlertTriangle className="text-red-500 text-sm" title="Broker offline" />
                        )}
                      </div>
                    </td>
                    {/* Broker */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {schedule.broker_display_name ? (
                        <div>
                          <div className="text-sm font-medium text-fg-1">
                            {schedule.broker_display_name}
                          </div>
                          {brokerSlugLabel && (
                            <div className="text-[10px] uppercase tracking-wide text-fg-2">
                              {brokerSlugLabel}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-fg-3 italic">System</span>
                      )}
                    </td>
                    {/* Country */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="ds-badge neutral xs" title={countryName}>
                        {flagUrl ? (
                          <img
                            src={flagUrl}
                            alt={`${countryName} flag`}
                            className="w-4 h-3 object-cover rounded-sm"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : scheduleCountry === 'ALL' ? '🌐' : scheduleCountry === 'UNKNOWN' ? '❓' : '🏳️'}
                        <span className="font-medium">{scheduleCountry}</span>
                      </div>
                    </td>
                    {/* Schedule (Local) */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isEditing ? (
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-1">
                            <select
                              value={editForm.hour}
                              onChange={(e) => setEditForm({ ...editForm, hour: parseInt(e.target.value, 10) })}
                              className="ds-input w-12"
                            >
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                              ))}
                            </select>
                            <span className="text-fg-2">:</span>
                            <select
                              value={editForm.minute}
                              onChange={(e) => setEditForm({ ...editForm, minute: parseInt(e.target.value, 10) })}
                              className="ds-input w-12"
                            >
                              {Array.from({ length: 60 }, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                              ))}
                            </select>
                          </div>
                          <select
                            value={editForm.dayOfWeek}
                            onChange={(e) => setEditForm({ ...editForm, dayOfWeek: e.target.value })}
                            className="ds-input"
                          >
                            {dayOfWeekOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          {!isManual && (
                            <label className="flex items-center gap-1 text-[10px] text-fg-2">
                              <input
                                type="checkbox"
                                checked={editForm.isEnabled}
                                onChange={(e) => setEditForm({ ...editForm, isEnabled: e.target.checked })}
                                className="h-3 w-3 rounded border-[var(--divider)] text-blue-600"
                              />
                              Enabled
                            </label>
                          )}
                        </div>
                      ) : isManual ? (
                        <span className="text-xs text-fg-3 italic">Not scheduled</span>
                      ) : (
                        <div>
                          <div className="text-sm font-medium text-fg-1">{timeInfo.local}</div>
                          {dayInfo !== 'Every day' && (
                            <div className="text-[10px] text-fg-2">{dayInfo}</div>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Next Run */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {schedule.next_run_utc ? (
                        <div>
                          <div className="text-sm text-fg-1">
                            {utcToLocal(schedule.next_run_utc)}
                          </div>
                          <div className="text-[10px] text-fg-2">
                            {getRelativeTime(schedule.next_run_utc)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-fg-3">N/A</span>
                      )}
                    </td>
                    {/* Last Run */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {schedule.last_run_utc ? (
                        <div>
                          <div className="text-sm text-fg-1">
                            {utcToLocal(schedule.last_run_utc)}
                          </div>
                          <div className="text-[10px] text-fg-2">
                            {getRelativeTime(schedule.last_run_utc)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-fg-3 italic">Never</span>
                      )}
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3">
                      {syncProgress ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`ds-badge xs ${
                              syncProgress.stage === 'completed' ? 'profit' :
                              syncProgress.stage === 'failed' ? 'loss' :
                              'info animate-pulse'
                            }`}>
                              {syncProgress.stage === 'completed' ? 'Done' :
                               syncProgress.stage === 'failed' ? 'Failed' : 'Running'}
                            </span>
                            <span className="text-[10px] text-fg-2">
                              {syncProgress.percentage.toFixed(0)}%
                            </span>
                          </div>
                          <div className="w-20 bg-bg-3 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all duration-300 ${
                                syncProgress.stage === 'completed' ? 'bg-green-500' :
                                syncProgress.stage === 'failed' ? 'bg-red-500' :
                                'bg-blue-500'
                              }`}
                              style={{ width: `${syncProgress.percentage}%` }}
                            ></div>
                          </div>
                          {/* Live progress message */}
                          {syncProgress.message && (
                            <div className="text-[9px] text-fg-2 truncate max-w-[120px]" title={syncProgress.message}>
                              {syncProgress.message}
                            </div>
                          )}
                          {/* Show stage if message not available */}
                          {!syncProgress.message && syncProgress.stage && syncProgress.stage !== 'completed' && syncProgress.stage !== 'failed' && (
                            <div className="text-[9px] text-fg-2 capitalize">
                              {syncProgress.stage.replace(/_/g, ' ')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <span className={`ds-badge xs ${
                            isManual
                              ? 'neutral'
                              : schedule.is_enabled
                                ? 'profit'
                                : 'neutral'
                          }`}>
                            {isManual ? 'Manual' : schedule.is_enabled ? 'Active' : 'Disabled'}
                          </span>
                          {lastSyncStatusLabel && (
                            <div className="text-[10px] text-fg-2">
                              Last: {lastSyncStatusLabel}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveSchedule(schedule)}
                              disabled={isSaving}
                              className={`p-1.5 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="Save"
                            >
                              <FiSave className="text-sm" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-1.5 text-fg-2 hover:text-fg-2 dark:text-fg-3 dark:hover:text-fg-3 hover:bg-bg-3 rounded"
                              title="Cancel"
                            >
                              <FiX className="text-sm" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEditing(schedule)}
                              className="p-1.5 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title="Edit Schedule"
                            >
                              <FiEdit2 className="text-sm" />
                            </button>
                            {schedule.broker_slug && schedule.supports_manual_trigger !== false && (
                              <button
                                onClick={() => triggerRowSync(schedule)}
                                disabled={syncProgress?.isActive}
                                className={`p-1.5 rounded transition-colors ${
                                  syncProgress?.isActive
                                    ? 'text-fg-3 cursor-not-allowed'
                                    : 'text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20'
                                }`}
                                title={syncProgress?.isActive ? 'Sync running' : 'Run sync now'}
                              >
                                <FiRefreshCw className={`text-sm ${syncProgress?.isActive ? 'animate-spin' : ''}`} />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Sync History from Database */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="ds-h3 text-fg-1 flex items-center gap-2">
            <FiDatabase className="text-blue-500" />
            Sync History
            {syncHistory.length > 0 && (
              <span className="text-xs font-normal text-fg-2">
                ({syncHistory.length} records)
              </span>
            )}
          </h3>
          <div className="flex items-center gap-3">
            {/* Source Filter */}
            <select
              value={syncHistoryFilter.source}
              onChange={(e) => {
                setSyncHistoryFilter(prev => ({ ...prev, source: e.target.value }));
                // Trigger re-fetch with new filter
                setTimeout(() => fetchSyncHistory(), 0);
              }}
              className="ds-input"
            >
              <option value="">All Sources</option>
              {brokerMetadata.map(broker => (
                <option key={broker.broker_slug} value={broker.broker_slug}>
                  {broker.display_name || broker.broker_slug.toUpperCase()}
                </option>
              ))}
            </select>
            {/* Status Filter */}
            <select
              value={syncHistoryFilter.status}
              onChange={(e) => {
                setSyncHistoryFilter(prev => ({ ...prev, status: e.target.value }));
                setTimeout(() => fetchSyncHistory(), 0);
              }}
              className="ds-input"
            >
              <option value="">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="PENDING">Pending</option>
            </select>
            <button
              type="button"
              onClick={fetchSyncHistory}
              disabled={syncHistoryLoading}
              className="btn btn-ghost btn-sm flex items-center gap-1"
            >
              <FiRefreshCw className={syncHistoryLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
        
        <div className="bg-white dark:bg-bg-1 rounded-xl border border-[var(--divider)] overflow-hidden shadow-sm">
          {/* overflow-x-auto too: this scrolled vertically only, so the min-w-full
              table's extra columns were clipped on a phone. */}
          <div className="max-h-96 overflow-y-auto overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-bg-3 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                    Started At
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                    Sync ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                    Status / Progress
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                    Master Stats
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                    Source Stats
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-fg-2 uppercase tracking-wider">
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {syncHistoryLoading ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center">
                      <div className="flex justify-center items-center gap-2 text-fg-2">
                        <FiLoader className="animate-spin" />
                        Loading sync history...
                      </div>
                    </td>
                  </tr>
                ) : syncHistory.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-fg-3">
                      <div className="flex flex-col items-center gap-2">
                        <FiDatabase className="text-2xl opacity-50" />
                        <span>No sync history found. Trigger a sync to see records here.</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  syncHistory.map((log) => {
                    // Check if this sync has live progress updates
                    const liveProgress = runningSyncs[log.sync_id];
                    const isRunning = liveProgress?.isActive;
                    const statusValue = log.status?.value || log.status || 'unknown';
                    const statusLower = statusValue.toLowerCase();
                    
                    return (
                      <tr 
                        key={log.sync_id}
                        className={`text-sm hover:bg-bg-3/50 ${
                          isRunning ? 'bg-blue-50 dark:bg-blue-900/10' :
                          statusLower === 'completed' ? '' :
                          statusLower === 'failed' ? 'bg-red-50/50 dark:bg-red-900/10' :
                          ''
                        }`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-fg-1">
                              {utcToLocal(log.started_at)}
                            </div>
                            <div className="text-xs text-fg-2">
                              {getRelativeTime(log.started_at)}
                            </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs font-mono text-fg-2 bg-bg-3 px-2 py-1 rounded">
                            {log.sync_id.substring(0, 8)}...
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="ds-badge neutral xs">
                            {log.broker_id > 0 ? `Broker #${log.broker_id}` : (log.app_name || 'Exchange')}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`ds-badge xs ${
                            log.sync_type === 'scheduled' ? 'role' : 'warn'
                          }`}>
                            {log.sync_type === 'scheduled' ? '⏰ Scheduled' : '👤 Manual'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {isRunning ? (
                            <div className="space-y-1.5">
                              <div className="flex items-center gap-2">
                                <span className="ds-badge info sm">
                                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                                  {liveProgress.stage}
                                </span>
                                <span className="text-xs font-medium text-fg-2">
                                  {liveProgress.percentage.toFixed(1)}%
                                </span>
                              </div>
                              <div className="w-32 bg-bg-3 rounded-full h-2">
                                <div
                                  className="h-2 rounded-full bg-blue-500 dark:bg-blue-400 transition-all duration-300"
                                  style={{ width: `${liveProgress.percentage}%` }}
                                ></div>
                              </div>
                              {liveProgress.message && (
                                <div className="text-xs text-fg-2 italic truncate max-w-xs">
                                  {liveProgress.message}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className={`ds-badge sm ${
                              statusLower === 'completed'
                                ? 'profit'
                                : statusLower === 'failed'
                                  ? 'loss'
                                  : statusLower === 'in_progress' || statusLower === 'in-progress'
                                    ? 'info'
                                    : 'neutral'
                            }`}>
                              {statusLower === 'completed' && <FiCheck />}
                              {statusLower === 'failed' && <FiXCircle />}
                              {(statusLower === 'in_progress' || statusLower === 'in-progress') && <FiLoader className="animate-spin" />}
                              {statusValue.replace(/_/g, ' ')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {(() => {
                            const liveMasterProcessed = liveProgress?.master_processed ?? liveProgress?.processed ?? 0;
                            const liveMasterTotal = liveProgress?.master_total ?? liveProgress?.total ?? 0;
                            const liveMasterAdded = liveProgress?.master_added ?? liveProgress?.added_count ?? 0;
                            const liveMasterUpdated = liveProgress?.master_updated ?? liveProgress?.updated_count ?? 0;
                            const liveMasterSkipped = liveProgress?.master_skipped ?? liveProgress?.skipped_count ?? 0;
                            const liveFailed = liveProgress?.failed_count ?? 0;
                            const showLiveMaster = isRunning && liveMasterTotal > 0;

                            const addedCount = showLiveMaster ? liveMasterAdded : log.added_count;
                            const updatedCount = showLiveMaster ? liveMasterUpdated : log.updated_count;
                            const skippedCount = showLiveMaster ? liveMasterSkipped : 0;
                            const failedCount = showLiveMaster ? liveFailed : log.failed_count;

                            return (
                              <div>
                                <div className="text-sm text-fg-1">
                                  {showLiveMaster
                                    ? `${liveMasterProcessed.toLocaleString()} / ${liveMasterTotal.toLocaleString()}`
                                    : log.total_instruments > 0
                                      ? log.total_instruments.toLocaleString()
                                      : '-'}
                                </div>
                                {(addedCount > 0 || updatedCount > 0 || skippedCount > 0 || failedCount > 0) && (
                                  <div className="text-xs text-fg-2 flex gap-2">
                                    {addedCount > 0 && <span className="text-green-600 dark:text-green-400">+{addedCount}</span>}
                                    {updatedCount > 0 && <span className="text-blue-600 dark:text-blue-400">~{updatedCount}</span>}
                                    {skippedCount > 0 && <span className="text-yellow-600 dark:text-yellow-400">⏭{skippedCount}</span>}
                                    {failedCount > 0 && <span className="text-red-600 dark:text-red-400">⚠️{failedCount}</span>}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {/* Source Stats (broker_source_instruments) */}
                          {(() => {
                            const liveSourceStats = (isRunning && liveProgress.source_stats) || null;
                            const derivedSourceStats = liveSourceStats || log.source_stats || log.error_details?.source_stats || null;
                            const sourceProcessed = liveProgress?.source_processed ?? derivedSourceStats?.processed ?? null;
                            const sourceTotal = liveProgress?.source_total ?? derivedSourceStats?.total ?? null;
                            const showLiveSource = isRunning && (liveProgress?.stage === 'updating_source') && (sourceTotal || sourceProcessed);

                            if (!derivedSourceStats && !showLiveSource) {
                              return <span className="text-sm text-fg-3">-</span>;
                            }

                            const created = (showLiveSource ? derivedSourceStats?.created : derivedSourceStats?.created) || 0;
                            const updated = (showLiveSource ? derivedSourceStats?.updated : derivedSourceStats?.updated) || 0;
                            const failed = (showLiveSource ? derivedSourceStats?.failed : derivedSourceStats?.failed) || 0;
                            const total = showLiveSource
                              ? (sourceTotal ?? derivedSourceStats?.total ?? 0)
                              : (derivedSourceStats?.total ?? 0);
                            const processedLabel = showLiveSource && sourceTotal
                              ? `${(sourceProcessed ?? 0).toLocaleString()} / ${sourceTotal.toLocaleString()}`
                              : (total > 0 ? total.toLocaleString() : '-');

                            return (
                              <div>
                                <div className="text-sm text-fg-1">
                                  {processedLabel}
                                </div>
                                {(created > 0 || updated > 0 || failed > 0) && (
                                  <div className="text-xs text-fg-2 flex gap-2">
                                    {created > 0 && <span className="text-emerald-600 dark:text-emerald-400">+{created}</span>}
                                    {updated > 0 && <span className="text-cyan-600 dark:text-cyan-400">~{updated}</span>}
                                    {failed > 0 && <span className="text-orange-600 dark:text-orange-400">✗{failed}</span>}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-2">
                          {log.duration_seconds != null ? (
                            log.duration_seconds >= 60 
                              ? `${Math.floor(log.duration_seconds / 60)}m ${log.duration_seconds % 60}s`
                              : `${log.duration_seconds}s`
                          ) : isRunning ? (
                            <span className="text-blue-500 dark:text-blue-400">Running...</span>
                          ) : '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Compact Help Text */}
      <div className="mt-4 text-xs text-fg-2 text-center">
        💡 Schedule changes take effect automatically within 30 seconds. Use "Force Restart" only if needed. Times are displayed in your local timezone ({userTimezone}).
      </div>
    </div>
  );
};

export default ScheduledRunsTab;